import { useTraduccion } from '@tallerya/i18n';
import { Boton, useBusquedaDiferida } from '@tallerya/ui';
import { useState } from 'react';
import { useBuscarClientes } from './datos';

// El dueño del activo, opcional (#119). Un buscador y no un combo: un taller
// tiene cientos de clientes, y recepción escribe el nombre o el documento.
//
// Cuando el formulario se abre desde la ficha del cliente (#105), el cliente ya
// viene elegido y solo se muestra su nombre.

interface Props {
  /** El id del cliente elegido, o vacío. */
  valor: string;
  /** Su nombre, para mostrarlo sin volver a pedirlo. */
  nombre: string | null;
  onElegir: (id: string, nombre: string) => void;
  onQuitar: () => void;
  /** El error de la API para el campo, ya traducido. */
  error?: string | undefined;
  /** El de `form.idDe('customerId')`: con él, el formulario sabe que este error se ve y le lleva el foco. */
  id: string;
}

export function SelectorDeCliente({ valor, nombre, onElegir, onQuitar, error, id }: Props) {
  const { t } = useTraduccion();
  const [texto, setTexto] = useState('');
  const [buscado, setBuscado] = useState('');
  const resultados = useBuscarClientes(buscado);

  useBusquedaDiferida(texto, setBuscado, { pendiente: texto !== buscado });

  if (valor) {
    return (
      <div className={`ui-campo ui-campo--largo${error ? ' ui-campo--error' : ''}`}>
        <span className="ui-campo__etiqueta">{t('admin.activos.cliente')}</span>
        <div className="activos__cliente">
          <strong>{nombre ?? t('app.cargando')}</strong>
          <Boton id={id} variante="texto" onClick={onQuitar}>
            {t('admin.activos.cliente.cambiar')}
          </Boton>
        </div>
        {error && <p className="ui-campo__ayuda">{error}</p>}
      </div>
    );
  }

  const lista = buscado.trim().length >= 2 ? (resultados.data ?? []) : [];

  return (
    <div className={`ui-campo ui-campo--largo${error ? ' ui-campo--error' : ''}`}>
      <label htmlFor={id} className="ui-campo__etiqueta">
        {t('admin.activos.cliente')}
      </label>
      <div className="ui-campo__caja">
        <input
          id={id}
          value={texto}
          autoComplete="off"
          placeholder={t('admin.activos.cliente.buscar')}
          aria-describedby={`${id}-ayuda`}
          onChange={(e) => setTexto(e.target.value)}
        />
      </div>
      {lista.length > 0 && (
        <ul className="activos__resultados" aria-label={t('admin.activos.cliente.resultados')}>
          {lista.map((c) => (
            <li key={String(c.id)}>
              <button
                type="button"
                className="activos__resultado"
                onClick={() => {
                  onElegir(String(c.id), c.name);
                  setTexto('');
                }}
              >
                <span>{c.name}</span>
                <small>{[c.code, c.primaryPhone].filter(Boolean).join(' · ')}</small>
              </button>
            </li>
          ))}
        </ul>
      )}
      <p id={`${id}-ayuda`} className="ui-campo__ayuda">
        {error ??
          (buscado.trim().length < 2
            ? t('admin.activos.cliente.ayuda')
            : !resultados.isFetching && lista.length === 0
              ? t('admin.activos.cliente.sinResultados')
              : null)}
      </p>
    </div>
  );
}
